import { Link, useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { CircleAlertIcon, FileQuestionIcon } from 'lucide-react'

import { Button, buttonVariants } from '@/components/ui/button'
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { errorSentence, isApiError } from '@/lib/api/errors'

/** Route error: one human sentence and Retry; never a stack trace. */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter()
  const notFound = isApiError(error) && error.status === 404
  if (notFound) return <DocumentNotFound />
  return (
    <div className="flex h-full items-center justify-center bg-app p-6">
      <Empty size="page">
        <EmptyMedia className="text-danger-fg">
          <CircleAlertIcon />
        </EmptyMedia>
        <EmptyHeader>
          <EmptyTitle>This screen couldn’t be shown.</EmptyTitle>
          <EmptyDescription>{errorSentence(error)}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button
            onClick={() => {
              reset()
              void router.invalidate()
            }}
          >
            Retry
          </Button>
          <Link to="/documents" className={buttonVariants({ variant: 'outline' })}>
            Back to documents
          </Link>
        </EmptyContent>
      </Empty>
    </div>
  )
}

export function DocumentNotFound() {
  return (
    <div className="flex h-full items-center justify-center bg-app p-6">
      <Empty size="page">
        <EmptyMedia>
          <FileQuestionIcon />
        </EmptyMedia>
        <EmptyHeader>
          <EmptyTitle>This document was deleted.</EmptyTitle>
          <EmptyDescription>It may have been removed by someone else. Go back to Documents.</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Link to="/documents" className={buttonVariants()}>
            Back to documents
          </Link>
        </EmptyContent>
      </Empty>
    </div>
  )
}

export function PageNotFound() {
  return (
    <div className="flex h-full items-center justify-center bg-app p-6">
      <Empty size="page">
        <EmptyMedia>
          <FileQuestionIcon />
        </EmptyMedia>
        <EmptyHeader>
          <EmptyTitle>This page doesn’t exist.</EmptyTitle>
          <EmptyDescription>Check the address, or go back to Documents.</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Link to="/documents" className={buttonVariants()}>
            Back to documents
          </Link>
        </EmptyContent>
      </Empty>
    </div>
  )
}
